// Focused admin screen: Jump It or Fix It stations, uploads, QR / print - without the full bonus-puzzle table.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import JumpItOrFixItStationsAdminEditor, { normalizeJumpItCustomLocation } from './JumpItOrFixItStationsAdminEditor';
import './BonusPuzzleManagement.css';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';

const JumpItStationManager = () => {
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [jumpPuzzles, setJumpPuzzles] = useState([]);
  const [selectedPuzzleId, setSelectedPuzzleId] = useState('');
  const [draftStations, setDraftStations] = useState([]);
  const [draftCustomLocations, setDraftCustomLocations] = useState([]);
  const [mergedSponsors, setMergedSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const eventsData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load events: ' + err.message);
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEvent) {
      setMergedSponsors([]);
      setSponsorsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSponsorsLoading(true);
    fetchSponsorsMergedForEvent(selectedEvent)
      .then((rows) => {
        if (!cancelled) setMergedSponsors(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMergedSponsors([]);
      })
      .finally(() => {
        if (!cancelled) setSponsorsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedEvent]);

  const reloadPuzzles = useCallback(async () => {
    if (!selectedEvent) {
      setJumpPuzzles([]);
      setSelectedPuzzleId('');
      setLoadingPuzzles(false);
      return;
    }
    setLoadingPuzzles(true);
    setError('');
    try {
      const snap = await getDocs(collection(firestore, 'bonusPuzzles'));
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.eventId === selectedEvent && p.type === 'jumpItOrFixIt');
      list.sort((a, b) => {
        const na = Number(a.number);
        const nb = Number(b.number);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return String(a.title || '').localeCompare(String(b.title || ''));
      });
      setJumpPuzzles(list);
      setSelectedPuzzleId((prev) => {
        if (list.length === 0) return '';
        if (prev && list.some((p) => p.id === prev)) return prev;
        return list[0].id;
      });
    } catch (err) {
      setError('Failed to load puzzles: ' + err.message);
      setJumpPuzzles([]);
    } finally {
      setLoadingPuzzles(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    reloadPuzzles();
  }, [reloadPuzzles]);

  useEffect(() => {
    if (!selectedPuzzleId) {
      setDraftStations([]);
      setDraftCustomLocations([]);
      return;
    }
    const p = jumpPuzzles.find((x) => x.id === selectedPuzzleId);
    if (!p) {
      setDraftStations([]);
      setDraftCustomLocations([]);
      return;
    }
    setDraftStations(Array.isArray(p.jumpItStations) ? p.jumpItStations : []);
    setDraftCustomLocations(
      Array.isArray(p.jumpItCustomSponsorLocations)
        ? p.jumpItCustomSponsorLocations.map(normalizeJumpItCustomLocation)
        : []
    );
  }, [selectedPuzzleId, jumpPuzzles]);

  // ─── Autosave (debounced) ─────────────────────────────────────────────────
  // Any change to stations or custom locations writes to Firestore ~600ms later.
  // The Save button is gone; users just edit and the puzzle persists.
  const autosaveTimer = useRef(null);
  const skipAutosaveRef = useRef(true); // skip the initial draft hydration from puzzle load
  const pendingRef = useRef({ stations: null, customs: null });

  const scheduleAutosave = useCallback(() => {
    if (!selectedPuzzleId) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      const stations = pendingRef.current.stations;
      const customs = pendingRef.current.customs;
      pendingRef.current = { stations: null, customs: null };
      if (stations == null && customs == null) return;
      setSaving(true);
      setError('');
      try {
        const patch = { updatedAt: serverTimestamp() };
        if (stations != null) patch.jumpItStations = stations;
        if (customs != null) patch.jumpItCustomSponsorLocations = customs;
        await updateDoc(doc(firestore, 'bonusPuzzles', selectedPuzzleId), patch);
        setJumpPuzzles((prev) =>
          prev.map((p) =>
            p.id === selectedPuzzleId
              ? {
                  ...p,
                  ...(stations != null ? { jumpItStations: stations } : {}),
                  ...(customs != null ? { jumpItCustomSponsorLocations: customs } : {}),
                }
              : p
          )
        );
        setSaveOk(true);
      } catch (err) {
        setError('Save failed: ' + err.message);
        setSaveOk(false);
      } finally {
        setSaving(false);
      }
    }, 600);
  }, [selectedPuzzleId]);

  const handleStationsChange = useCallback((next) => {
    setDraftStations(next);
    if (skipAutosaveRef.current) return;
    pendingRef.current.stations = next;
    setSaveOk(false);
    scheduleAutosave();
  }, [scheduleAutosave]);

  const handleCustomLocationsChange = useCallback((next) => {
    setDraftCustomLocations(next);
    if (skipAutosaveRef.current) return;
    pendingRef.current.customs = next;
    setSaveOk(false);
    scheduleAutosave();
  }, [scheduleAutosave]);

  // After hydrating from a (re)loaded puzzle, allow autosave again on next user edit.
  useEffect(() => {
    skipAutosaveRef.current = true;
    const t = setTimeout(() => { skipAutosaveRef.current = false; }, 0);
    return () => clearTimeout(t);
  }, [selectedPuzzleId, jumpPuzzles]);

  // Flush pending autosave on unmount or puzzle switch.
  useEffect(() => () => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
  }, []);

  const selectedPuzzle = useMemo(
    () => jumpPuzzles.find((p) => p.id === selectedPuzzleId) || null,
    [jumpPuzzles, selectedPuzzleId]
  );

  const eventTitle = activeEvents.find((e) => e.id === selectedEvent)?.title || '';

  if (loadingEvents) {
    return (
      <div className="bonus-puzzle-management">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="bonus-puzzle-management">
      <h2>Jump It / Fix It - Station manager</h2>
      <p className="form-hint" style={{ maxWidth: 640, marginBottom: 16 }}>
        Lay out stations, upload images, and print QR signs for this puzzle type. Public code, point value, and enabling
        the puzzle are still under{' '}
        <Link to="/admin/bonus-puzzle-management">Bonus Puzzles</Link>.
      </p>
      {error && <p className="error">{error}</p>}
      <p style={{ marginBottom: 8, fontSize: '0.85rem', color: saving ? '#92400e' : (saveOk ? '#166534' : '#6b7280') }}>
        {saving ? '● Saving…' : (saveOk ? '✓ All changes saved.' : 'Edits autosave as you type.')}
      </p>

      <div className="dropdown-container">
        <label htmlFor="jumpit-manager-event">Event</label>
        <select
          id="jumpit-manager-event"
          value={selectedEvent}
          onChange={(e) => {
            setSelectedEvent(e.target.value);
            setSelectedPuzzleId('');
            setSaveOk(false);
          }}
        >
          {activeEvents.map((event) => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>
      </div>

      {loadingPuzzles && <p>Loading puzzles...</p>}

      {!loadingPuzzles && selectedEvent && jumpPuzzles.length === 0 && (
        <p>
          No Jump It or Fix It puzzle for this event. Add one under{' '}
          <Link to="/admin/bonus-puzzle-management">Bonus Puzzles</Link> (set type to <strong>jumpItOrFixIt</strong>).
        </p>
      )}

      {!loadingPuzzles && jumpPuzzles.length > 0 && (
        <>
          <div className="dropdown-container" style={{ marginTop: 12 }}>
            <label htmlFor="jumpit-manager-puzzle">Puzzle</label>
            <select
              id="jumpit-manager-puzzle"
              value={selectedPuzzleId}
              onChange={(e) => setSelectedPuzzleId(e.target.value)}
            >
              {jumpPuzzles.map((p) => (
                <option key={p.id} value={p.id}>
                  #{p.number} - {p.title || 'Untitled'}
                  {p.enabled === false ? ' (disabled)' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedPuzzle && (
            <>
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  textAlign: 'left',
                  fontSize: 14,
                }}
              >
                <strong>Public code:</strong>{' '}
                {selectedPuzzle.publicCode ? (
                  <code>{selectedPuzzle.publicCode}</code>
                ) : (
                  <span style={{ color: '#b45309' }}>Not set (add one in Bonus Puzzles).</span>
                )}
                {selectedPuzzle.location ? (
                  <>
                    {' '}
                    &middot; <strong>Location (print footer):</strong> {selectedPuzzle.location}
                  </>
                ) : null}
              </div>

              <div className="form-group" style={{ marginTop: 20 }}>
                <JumpItOrFixItStationsAdminEditor
                  stations={draftStations}
                  onChange={handleStationsChange}
                  customLocations={draftCustomLocations}
                  onCustomLocationsChange={handleCustomLocationsChange}
                  mergedSponsors={mergedSponsors}
                  sponsorsLoading={sponsorsLoading}
                  publicCode={selectedPuzzle.publicCode || ''}
                  eventId={selectedEvent}
                  printEventTitle={eventTitle}
                  printPuzzleTitle={
                    (selectedPuzzle.title || '').trim() ||
                    (selectedPuzzle.number != null && selectedPuzzle.number !== ''
                      ? `Bonus puzzle ${selectedPuzzle.number}`
                      : '')
                  }
                  printFooterLeft={(selectedPuzzle.location || '').trim() || eventTitle || ''}
                />
              </div>

            </>
          )}
        </>
      )}
    </div>
  );
};

export default JumpItStationManager;
